<template>
  <div class="prescriberProxyUserDetails">
    <div class="grid py-4" v-if="showProxy">
      <card>
        <template v-slot>
          <div class="flex item-start">
            <div class="flex-shrink-0 content-center"><v-account-group-outline></v-account-group-outline></div>
            <div class="flex-1">
              <div class="text-2xl font-semibold leading-6 flex-1 mx-2">Volmachtgevers</div>
            </div>
          </div>
          <div class="grid my-4">
            <div v-for="(proxy, index) in proxyUserDetails" :key="index" class="border-b-2 mb-4">
              <div class="grid grid-cols-2 w-1/2">
                <div>
                  <div class="grid grid-rows-2">
                    <div class="text-black mb-2">
                      {{ getProxyName(proxy) }}
                    </div>
                    <div class="text-black-700">{{ proxy.email }}</div>
                  </div>
                </div>
                <div>
                  <v-button md cta @click.prevent="deleteProxyUserId = proxy.authorizedUserId">
                    <v-delete-icon></v-delete-icon>
                  </v-button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </card>
    </div>
    <div class="grid py-4" v-if="addProxy">
      <card>
        <template v-slot>
          <div class="flex item-start">
            <div class="flex-shrink-0 content-center"><v-plus></v-plus></div>
            <div class="flex-1">
              <div class="text-2xl font-semibold leading-6 flex-1 mx-2">Volmachtgever toevoegen</div>
            </div>
          </div>
          <div class="grid my-2">
            <div class="pt-2">
              <div class="flex items-center px-2 py-2">
                <div class="w-2/4">
                  <p>Volledig e-mailadres van een volmachtgever</p>
                  <div class="grid grid-cols-2">
                    <div>
                      <v-input-group-simple-2
                        id="email"
                        v-model="searchProxyEmail"
                        type="text"
                        :rules="{
                          required: true,
                          email: true,
                          regex: /@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/
                        }"
                      />
                    </div>
                    <div class="ml-4 mt-1 mb-1">
                      <v-button md hollow @click.prevent="searchProxyUsers"> Zoeken <v-search-icon /></v-button>
                    </div>
                  </div>
                  <p>Begin met zoeken naar een volmachtgever op basis van een volledig e-mailadres.</p>
                </div>
              </div>
            </div>
            <div class="grid mt-2 mb-8">
              <div v-if="searchProxyUserDetails.length > 0">
                <div v-for="(proxy, index) in searchProxyUserDetails" :key="index" class="border-b-2 mb-4">
                  <div class="grid grid-cols-2 w-1/2">
                    <div>
                      <div class="grid grid-rows-2">
                        <div class="text-black mb-2">
                          {{ getProxyName(proxy) }}
                        </div>
                        <div class="text-black-700">{{ proxy.email }}</div>
                      </div>
                    </div>
                    <div>
                      <v-button md cta icon-left @click.prevent="addProxyUser(proxy.userId)">
                        <v-plus-circle-icon title="" />Toevoegen
                      </v-button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="showNoResult">
                <Alert
                  title="Let op!"
                  message="Opgelet! Did email adres is niet bij ons bekend als een voorschrijver. Uw volmachtgever moet voor het voorschrijfportaal een account hebben om als volmachtiger ingegeven te worden. Klik op “Collega toevoegen” om uw collega aan te melden."
                  type="error"
                ></Alert>
              </div>
            </div>
            <div class="flex justify-end">
              <v-button md cta hollow transparent @click.prevent="goBackToDepartment"> Annuleren </v-button>
              <v-button md cta v-if="showNoResult" @click="$router.push('/collega-registreren')">
                Collega toevoegen
              </v-button>
            </div>
          </div>
        </template>
      </card>
    </div>
  </div>
  <modal-dialog @update:is-active="handleUpdate" :modalOpenStatus="deleteProxyUserId !== null">
    <template v-slot:title>Bevestiging Verwijderen</template>
    <template v-slot:description> Weet u zeker dat u deze volmachtgever wil verwijderen? </template>
  </modal-dialog>
</template>
<script>
import VAccountGroupOutline from 'icons/AccountGroupOutline';
import VSearchIcon from 'icons/Magnify';
import VDeleteIcon from 'icons/Delete';
import VPlusCircleIcon from 'icons/PlusCircle';
import VPlus from 'icons/Plus';
import { ref, watch, getCurrentInstance, onMounted } from 'vue';
import axios from 'axios';
import { useForm } from 'vee-validate';

export default {
  name: 'PrescriberProxyUserDetails',
  components: {
    VAccountGroupOutline,
    VSearchIcon,
    VDeleteIcon,
    VPlusCircleIcon,
    VPlus
  },
  props: {
    addProxyUserStatus: {
      type: Boolean,
      default: false
    },
    proxyUserDetails: {
      type: Array,
      required: true
    },
    userDetails: {
      type: Object,
      required: true
    },
    department: {
      type: Object,
      required: true
    }
  },
  emits: ['setDepartmentBreadCrumb', 'goBackToDepartment', 'showDepartmentProxyDetails'], // Declare emitted events
  setup(props, { emit }) {
    const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;
    const { validate } = useForm();
    const showProxy = ref(false);
    const deleteProxyUserId = ref(null);
    const addProxy = ref(false);
    const searchProxyUserDetails = ref([]);
    const searchProxyEmail = ref(null);
    const showNoResult = ref(false);

    onMounted(() => {
      const hasProxies = props.proxyUserDetails.length > 0;
      showProxy.value = hasProxies;
      addProxy.value = !hasProxies;
    });

    const searchProxyUsers = async () => {
      const result = await validate();
      if (result.valid) {
        axios.get('UserV2/GetAllProxyUsersByEmail?email=' + searchProxyEmail.value).then((response) => {
          if (response && response.data && response.data.length > 0) {
            searchProxyUserDetails.value = response.data;
          } else {
            showNoResult.value = true;
          }
        });
      }
    };

    const setDepartmentBreadCrumb = () => {
      emit('setDepartmentBreadCrumb');
    };

    const goBackToDepartment = () => {
      emit('goBackToDepartment');
      setDepartmentBreadCrumb();
    };

    const addProxyUser = (proxyUserId) => {
      axios
        .post('UserV2/AddProxyUser', {
          ppwrecid: props.userDetails.externalId + '-' + props.department.externalId,
          userId: proxyUserId
        })
        .then((response) => {
          if (response && response.data) {
            $emitter.emit('notification', {
              title: 'wijzigingen succesvol opgeslagen'
            });
            searchProxyUserDetails.value = [];
            emit('showDepartmentProxyDetails');
          }
        });
    };

    const handleUpdate = (isActive) => {
      if (isActive) {
        deleteProxyUser(deleteProxyUserId.value);
      } else {
        deleteProxyUserId.value = null;
      }
    };

    const getProxyName = (proxy) => {
      let proxyName = proxy.lastName;
      if (proxy.initials) {
        proxyName += ', ( ' + proxy.initials + ' )';
      }
      if (proxy.firstName) {
        proxyName += (proxy.initials ? ' ' : ', ') + proxy.firstName;
      }
      return proxyName;
    };

    const deleteProxyUser = (authorizedUserId) => {
      axios
        .post('UserV2/DeleteProxyUser', {
          authorizedUserId: authorizedUserId
        })
        .then((response) => {
          deleteProxyUserId.value = null;
          if (response && response.data) {
            $emitter.emit('notification', {
              title: 'wijzigingen succesvol opgeslagen'
            });
            if (props.proxyUserDetails.length == 1) {
              showProxy.value = false;
              addProxy.value = true;
            } else {
              emit('showDepartmentProxyDetails');
            }
          }
        });
    };

    watch(searchProxyEmail, (oldVal, newVal) => {
      if (newVal !== oldVal) {
        showNoResult.value = false;
        searchProxyUserDetails.value = [];
      }
    });

    watch(
      () => props.addProxyUserStatus,
      (newVal) => {
        addProxy.value = newVal;
        showProxy.value = !newVal;
      }
    );

    return {
      emit,
      showProxy,
      deleteProxyUser,
      deleteProxyUserId,
      handleUpdate,
      addProxy,
      addProxyUser,
      searchProxyEmail,
      searchProxyUsers,
      searchProxyUserDetails,
      getProxyName,
      showNoResult,
      goBackToDepartment,
      setDepartmentBreadCrumb
    };
  }
};
</script>
<style lang="scss" scoped>
.prescriberProxyUserDetails {
  color: $teal-darkest;
  :deep(.form-group-simple label) {
    color: $black;
    font-weight: 700;
    font-size: 16px;
  }
  .material-design-icon {
    fill: $teal-darkest;
  }
}
</style>
